// Pixel-art lettering for the logo title («КОРОВАНЫ» on the splash and the
// menu) and the menu subtitle. The font stays the source: the text is drawn
// with the real font at 4× the target cell count, each 4×4 block becomes one
// cell, in or out by coverage (no soft edges), and the cells are coloured
// from a tiny fixed palette:
//
//   ink   — horizontal bands, light at the top to deep at the bottom
//   shadow — the ink shifted down `shadowCells`, one flat dark colour
//   outline — one dark cell around ink + shadow
//   glow  — optional separate mask: ink grown by `glowCells`, dithered rim
//
// One cell = PIXEL_SCALE CSS px, the guard sprites' pixel. Results are cached
// per (text, style) — the text already differs per language, so a language
// switch or a new size is just another key.

import { PIXEL_SCALE } from './pixel-art.js';

const SS = 4;

function hexRgb(hex) {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

// Grow a mask by `r` cells (round brush). With `dither`, cells in the
// outermost ring are kept on a checkerboard only — a stepped soft edge.
export function dilate(mask, w, h, r, dither = false) {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x]) { out[y * w + x] = 1; continue; }
      let best = Infinity;
      for (let dy = -r; dy <= r; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -r; dx <= r; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w || !mask[yy * w + xx]) continue;
          const d = dx * dx + dy * dy;
          if (d < best) best = d;
        }
      }
      if (best > (r + 0.5) * (r + 0.5)) continue;
      if (dither && best > (r - 0.5) * (r - 0.5) && (x + y) % 2) continue;
      out[y * w + x] = 1;
    }
  }
  return out;
}

// Pure: ink mask → RGBA cells. Every pixel is fully opaque in a palette
// colour or fully transparent.
export function composeTextCells(ink, w, h, style) {
  const bands = (style.bands || ['#ffd700']).map(hexRgb);
  const shadowCells = style.shadow ? (style.shadowCells ?? 1) : 0;
  let top = h;
  let bottom = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) if (ink[y * w + x]) { if (y < top) top = y; if (y > bottom) bottom = y; }
  }
  const shadow = new Uint8Array(w * h);
  for (let s = 1; s <= shadowCells; s++) {
    for (let y = 0; y + s < h; y++) {
      for (let x = 0; x < w; x++) if (ink[y * w + x]) shadow[(y + s) * w + x] = 1;
    }
  }
  const body = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) body[i] = ink[i] | shadow[i];
  const ring = style.outline ? dilate(body, w, h, 1) : body;

  const data = new Uint8ClampedArray(w * h * 4);
  const put = (i, c) => { data[i * 4] = c[0]; data[i * 4 + 1] = c[1]; data[i * 4 + 2] = c[2]; data[i * 4 + 3] = 255; };
  const outline = style.outline && hexRgb(style.outline);
  const shade = style.shadow && hexRgb(style.shadow);
  const span = Math.max(1, bottom - top + 1);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (ink[i]) put(i, bands[Math.min(bands.length - 1, Math.floor(((y - top) / span) * bands.length))]);
      else if (shadow[i]) put(i, shade);
      else if (ring[i]) put(i, outline);
    }
  }
  return data;
}

// Browser: draw `text` in the real font and turn it into an ink mask.
function rasterTextMask(text, style, pad) {
  const size = style.sizeCells * SS;
  const font = `${style.weight || 'bold'} ${size}px ${style.family || 'monospace'}`;
  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) return null;
  probe.font = font;
  if (style.letterSpacingEm && 'letterSpacing' in probe) {
    probe.letterSpacing = `${style.letterSpacingEm * size}px`;
  }
  const m = probe.measureText(text);
  const asc = Math.ceil(m.actualBoundingBoxAscent || size * 0.8);
  const desc = Math.ceil(m.actualBoundingBoxDescent || size * 0.2);
  const left = Math.ceil(m.actualBoundingBoxLeft || 0);
  const inkW = Math.ceil((m.actualBoundingBoxRight || m.width) + left);
  const w = Math.ceil(inkW / SS) + 2 * pad;
  const h = Math.ceil((asc + desc) / SS) + 2 * pad;
  const work = document.createElement('canvas');
  work.width = w * SS;
  work.height = h * SS;
  const ctx = work.getContext('2d');
  ctx.font = font;
  if (style.letterSpacingEm && 'letterSpacing' in ctx) ctx.letterSpacing = probe.letterSpacing;
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(text, pad * SS + left, pad * SS + asc);
  const src = ctx.getImageData(0, 0, work.width, work.height).data;
  const mask = new Uint8Array(w * h);
  for (let cy = 0; cy < h; cy++) {
    for (let cx = 0; cx < w; cx++) {
      let a = 0;
      for (let y = 0; y < SS; y++) {
        for (let x = 0; x < SS; x++) a += src[((cy * SS + y) * work.width + cx * SS + x) * 4 + 3];
      }
      mask[cy * w + cx] = a / (SS * SS * 255) >= 0.5 ? 1 : 0;
    }
  }
  return { mask, w, h };
}

function toCanvas(data, w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d').putImageData(new ImageData(data, w, h), 0, 0);
  return c;
}

const _cache = new Map();
const CACHE_MAX = 32;

// { canvas, glow, w, h } in cells (glow is null without style.glow), or
// null where there is no DOM (headless tests) — callers fall back to text.
// `style` must carry a stable `key` naming the recipe.
export function pixelTextSprite(text, style) {
  if (typeof document === 'undefined') return null;
  const key = `${style.key}|${style.sizeCells}|${text}`;
  let sprite = _cache.get(key);
  if (sprite) return sprite;
  const pad = (style.shadow ? style.shadowCells ?? 1 : 0) + 1 + (style.glowCells || 0) + 1;
  const raster = rasterTextMask(text, style, pad);
  if (!raster) return null;
  const { mask, w, h } = raster;
  const canvas = toCanvas(composeTextCells(mask, w, h, style), w, h);
  let glow = null;
  if (style.glow) {
    const grown = dilate(mask, w, h, style.glowCells || 2, true);
    const c = hexRgb(style.glow);
    const data = new Uint8ClampedArray(w * h * 4);
    for (let i = 0; i < w * h; i++) {
      if (!grown[i]) continue;
      data[i * 4] = c[0]; data[i * 4 + 1] = c[1]; data[i * 4 + 2] = c[2]; data[i * 4 + 3] = 255;
    }
    glow = toCanvas(data, w, h);
  }
  if (_cache.size >= CACHE_MAX) _cache.clear();
  sprite = { canvas, glow, w, h };
  _cache.set(key, sprite);
  return sprite;
}

// Browser: replace an HTML heading's text with its pixel-art rendering,
// same box (the heading keeps its size, so nothing reflows) and same font.
// The heading's text stays in the DOM for screen readers; on resize the
// sprite is rebuilt for the new font size. Returns a function that stops
// the rebuilding (for a heading that is later removed).
export function pixelateHeading(el, style) {
  let canvas = null;
  const build = () => {
    el.style.color = '';
    el.style.textShadow = '';
    const cs = getComputedStyle(el);
    const fontPx = parseFloat(cs.fontSize);
    const sizeCells = Math.max(8, Math.round(fontPx / PIXEL_SCALE));
    const letterSpacingEm = cs.letterSpacing === 'normal' ? 0 : parseFloat(cs.letterSpacing) / fontPx;
    const sprite = pixelTextSprite(el.textContent.trim(), {
      ...style,
      key: `${style.key}|${cs.fontFamily}|${letterSpacingEm.toFixed(3)}`,
      family: cs.fontFamily,
      weight: cs.fontWeight,
      sizeCells,
      letterSpacingEm,
    });
    if (!sprite) return;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);image-rendering:pixelated;pointer-events:none';
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
      el.appendChild(canvas);
    }
    canvas.width = sprite.w;
    canvas.height = sprite.h;
    canvas.style.width = `${sprite.w * PIXEL_SCALE}px`;
    canvas.style.height = `${sprite.h * PIXEL_SCALE}px`;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(sprite.canvas, 0, 0);
    // Hide the vector glyphs but keep their box (and the text for a11y).
    el.style.color = 'transparent';
    el.style.textShadow = 'none';
  };
  try { build(); } catch (_e) { /* keep the vector heading */ }
  const onResize = () => {
    try { build(); } catch (_e) { /* keep the vector heading */ }
  };
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}
